import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { EditorConfigService } from '../../../features/editor/services/editor-config.service';
import { VarDef } from '../../models';
import { argKeyError, findExistingKey, normalizeArgKey } from '../../utils/arg-key';

interface PickerOption {
	label: string;
	value: string | null;
	separator?: boolean;
}

@Component({
	selector: 'app-var-picker',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, Select, DialogModule, ButtonModule, InputTextModule],
	template: `
		<div class="flex gap-1 items-center">
			<p-select
				class="flex-1 min-w-0"
				[options]="options()"
				optionLabel="label"
				optionValue="value"
				appendTo="body"
				[ngModel]="value() ?? null"
				(ngModelChange)="valueChange.emit($event)"
				placeholder="— none —"
				styleClass="p-select-fluid"
			/>
			<button class="shrink-0 text-wb-accent hover:text-wb-accent-2 transition-colors bg-transparent border-0 cursor-pointer text-[0.85rem] px-1" (click)="openDialog()" title="New variable">
				<i class="pi pi-plus"></i>
			</button>
		</div>

		<p-dialog
			header="New Variable"
			[visible]="dialogVisible()"
			(visibleChange)="dialogVisible.set($event)"
			[modal]="true"
			[style]="{ width: '400px' }"
			appendTo="body"
		>
			<div class="flex flex-col gap-3 pt-2">
				<div>
					<label class="text-[0.75rem] text-wb-muted block mb-1">Key</label>
					<input pInputText class="w-full" [ngModel]="newKey()" (ngModelChange)="newKey.set($event)" placeholder="e.g. name" />
					@if (newKey().trim() && keyError()) {
						<small class="text-red-400 mt-1 block">{{ keyError() }}</small>
					} @else {
						<small class="text-wb-muted mt-1 block">Lower case, no spaces — underscores and hyphens are fine.</small>
					}
				</div>
				<div>
					<label class="text-[0.75rem] text-wb-muted block mb-1">Type</label>
					<select class="w-full bg-[var(--wb-bg-surface)] border border-[var(--wb-border)] rounded-lg text-wb-text text-[0.85rem] px-3 py-2 outline-none"
						[ngModel]="newType()" (ngModelChange)="newType.set($event)">
						<option value="string">string</option>
						<option value="number">number</option>
						<option value="boolean">boolean</option>
					</select>
				</div>
				<div>
					<label class="text-[0.75rem] text-wb-muted block mb-1">Default Value</label>
					<input pInputText class="w-full" [ngModel]="newDefaultValue()" (ngModelChange)="newDefaultValue.set($event)" />
				</div>
				<div>
					<label class="text-[0.75rem] text-wb-muted block mb-1">Description</label>
					<input pInputText class="w-full" [ngModel]="newDescription()" (ngModelChange)="newDescription.set($event)" />
				</div>
			</div>
			<ng-template #footer>
				<div class="flex justify-end gap-2 pt-2">
					<p-button label="Cancel" severity="secondary" (click)="dialogVisible.set(false)" />
					<p-button label="Create & Select" [disabled]="!!keyError()" (click)="confirmCreate()" />
				</div>
			</ng-template>
		</p-dialog>
	`,
})
export class VarPickerComponent {
	private configService = inject(EditorConfigService);

	value = input<string | null>(null);
	valueChange = output<string | null>();

	dialogVisible = signal(false);
	newKey = signal('');
	newType = signal('string');
	newDefaultValue = signal('');
	newDescription = signal('');

	protected options = computed<PickerOption[]>(() => {
		const defs = this.configService.varDefs();
		const result: PickerOption[] = [
			{ label: '— none —', value: null },
		];
		for (const def of defs) {
			result.push({ label: `${def.key} (${def.type})`, value: def.key });
		}
		return result;
	});

	protected keyExists(): boolean {
		return !!findExistingKey(this.newKey(), this.configService.varDefs().map((d) => d.key));
	}

	/** DEV-651: why this key cannot be used, or null. Drives the inline message. */
	protected keyError(): string | null {
		return argKeyError(this.newKey(), this.configService.varDefs().map((d) => d.key));
	}

	protected openDialog(): void {
		this.newKey.set('');
		this.newType.set('string');
		this.newDefaultValue.set('');
		this.newDescription.set('');
		this.dialogVisible.set(true);
	}

	protected confirmCreate(): void {
		const key = normalizeArgKey(this.newKey());
		if (this.keyError()) return;
		const def: VarDef = {
			key,
			type: this.newType() as VarDef['type'],
			defaultValue: this.newDefaultValue().trim() || undefined,
			description: this.newDescription().trim() || undefined,
		};
		this.configService.addVarDef(def);
		this.dialogVisible.set(false);
		this.valueChange.emit(key);
	}
}
